import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowRight, Mail, Linkedin, Download, CheckCircle, AlertCircle, Calendar, FileText, Phone, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    organization: '',
    job_title: '',
    email: '',
    phone: '',
    service_type: '',
    goals: '',
    preferred_time: '',
    honeypot: '', // Spam protection - hidden field
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceTypes = [
    'Executive Leadership Coaching',
    'New Superintendent Coaching',
    'Leadership Development',
    'Strategic Planning',
    'Professional Learning',
    'Middle School Academic Improvement',
    'Keynote Speaking',
    'Other',
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData.organization.trim()) {
      newErrors.organization = 'Organization is required';
    }
    
    if (!formData.job_title.trim()) {
      newErrors.job_title = 'Job title is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-\(\)\+]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.service_type) {
      newErrors.service_type = 'Please select a service type';
    }
    
    if (!formData.goals.trim()) {
      newErrors.goals = 'Please tell us about your goals';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/strategy-session`, formData);
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: response.data.message,
        });
        setFormData({
          full_name: '',
          organization: '',
          job_title: '',
          email: '',
          phone: '',
          service_type: '',
          goals: '',
          preferred_time: '',
          honeypot: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or contact us directly at legacyleadersgroupal@gmail.com',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloads = [
    { title: 'Company Profile', desc: 'Learn about Legacy Leadership Group', icon: FileText },
    { title: 'Capability Statement', desc: 'Our qualifications and expertise', icon: FileText },
    { title: 'District Leadership Assessment', desc: 'Self-assessment for districts', icon: FileText },
  ];

  return (
    <div className="pt-20 lg:pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="contact-hero">
        <div className="container-premium">
          <div className="max-w-3xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="overline text-[#4F8A2F] mb-4"
            >
              Let's Connect
            </motion.p>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Schedule a Strategy Session
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-300 text-lg lg:text-xl leading-relaxed"
            >
              Ready to discuss how Legacy Leadership Group can partner with your district? Complete the form below and we'll schedule a confidential strategy session.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-white" data-testid="contact-form-section">
        <div className="container-premium">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F7F8F6] rounded-2xl p-8 lg:p-12">
                <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#172033] mb-2">
                  Request a Strategy Session
                </h2>
                <p className="text-slate-600 mb-8">
                  Share your goals and we'll respond within 48 hours.
                </p>

                {status.type === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
                    data-testid="success-message"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-green-800 mb-2">
                      Request Received!
                    </h3>
                    <p className="text-green-700 leading-relaxed">
                      {status.message}
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="strategy-session-form">
                    {/* Honeypot field - hidden from users, catches bots */}
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleChange}
                      style={{ display: 'none' }}
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    {/* Row 1: Full Name & Organization */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="full_name" className="font-body text-sm text-slate-700 mb-2 block">
                          Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          type="text"
                          value={formData.full_name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          className={`bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12 ${errors.full_name ? 'border-red-500' : ''}`}
                          data-testid="input-full-name"
                        />
                        {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="organization" className="font-body text-sm text-slate-700 mb-2 block">
                          Organization / School District <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="organization"
                          name="organization"
                          type="text"
                          value={formData.organization}
                          onChange={handleChange}
                          placeholder="Your school district or organization"
                          className={`bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12 ${errors.organization ? 'border-red-500' : ''}`}
                          data-testid="input-organization"
                        />
                        {errors.organization && <p className="text-red-500 text-xs mt-1">{errors.organization}</p>}
                      </div>
                    </div>

                    {/* Row 2: Job Title & Email */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="job_title" className="font-body text-sm text-slate-700 mb-2 block">
                          Job Title <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="job_title"
                          name="job_title"
                          type="text"
                          value={formData.job_title}
                          onChange={handleChange}
                          placeholder="e.g., Superintendent, Principal"
                          className={`bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12 ${errors.job_title ? 'border-red-500' : ''}`}
                          data-testid="input-job-title"
                        />
                        {errors.job_title && <p className="text-red-500 text-xs mt-1">{errors.job_title}</p>}
                      </div>
                      <div>
                        <Label htmlFor="email" className="font-body text-sm text-slate-700 mb-2 block">
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className={`bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12 ${errors.email ? 'border-red-500' : ''}`}
                          data-testid="input-email"
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Row 3: Phone & Service Type */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="font-body text-sm text-slate-700 mb-2 block">
                          Phone Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 555-5555"
                          className={`bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12 ${errors.phone ? 'border-red-500' : ''}`}
                          data-testid="input-phone"
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                      </div>
                      <div>
                        <Label htmlFor="service_type" className="font-body text-sm text-slate-700 mb-2 block">
                          Service Requested <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="service_type"
                          name="service_type"
                          value={formData.service_type}
                          onChange={handleChange}
                          className={`w-full bg-white border border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12 px-4 text-slate-700 ${errors.service_type ? 'border-red-500' : ''}`}
                          data-testid="input-service-type"
                        >
                          <option value="">Select a service</option>
                          {serviceTypes.map((type, i) => (
                            <option key={i} value={type}>{type}</option>
                          ))}
                        </select>
                        {errors.service_type && <p className="text-red-500 text-xs mt-1">{errors.service_type}</p>}
                      </div>
                    </div>

                    {/* Row 4: Goals */}
                    <div>
                      <Label htmlFor="goals" className="font-body text-sm text-slate-700 mb-2 block">
                        Tell Us About Your Goals <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="goals"
                        name="goals"
                        value={formData.goals}
                        onChange={handleChange}
                        placeholder="What challenges are you facing? What outcomes are you hoping to achieve? How can we help your district succeed?"
                        rows={5}
                        className={`bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl resize-none ${errors.goals ? 'border-red-500' : ''}`}
                        data-testid="input-goals"
                      />
                      {errors.goals && <p className="text-red-500 text-xs mt-1">{errors.goals}</p>}
                    </div>

                    {/* Row 5: Preferred Meeting Time */}
                    <div>
                      <Label htmlFor="preferred_time" className="font-body text-sm text-slate-700 mb-2 block">
                        Preferred Meeting Date/Time <span className="text-slate-400">(optional)</span>
                      </Label>
                      <Input
                        id="preferred_time"
                        name="preferred_time"
                        type="text"
                        value={formData.preferred_time}
                        onChange={handleChange}
                        placeholder="e.g., Tuesday, Jan 15 at 2:00 PM or Mornings preferred"
                        className="bg-white border-slate-200 focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A] rounded-xl h-12"
                        data-testid="input-preferred-time"
                      />
                    </div>

                    {/* Error Message */}
                    {status.type === 'error' && (
                      <div
                        className="flex items-center gap-3 p-4 rounded-xl bg-red-50 text-red-800 border border-red-200"
                        data-testid="error-message"
                      >
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="font-body text-sm">{status.message}</span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full py-5 font-body text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Request My Strategy Session
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>

                    <p className="text-center text-slate-500 text-xs">
                      By submitting this form, you agree to be contacted by Legacy Leadership Group.
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Direct Contact */}
              <div>
                <h3 className="font-display text-xl font-bold text-[#172033] mb-6">Direct Contact</h3>
                <div className="space-y-4">
                  <a
                    href="mailto:legacyleadersgroupal@gmail.com"
                    className="flex items-center gap-4 p-4 bg-[#F7F8F6] rounded-xl hover:bg-slate-100 transition-colors"
                    data-testid="contact-email-link"
                  >
                    <div className="w-12 h-12 bg-[#0B1F3A] flex items-center justify-center rounded-xl flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                      <p className="font-body text-[#172033] font-medium">legacyleadersgroupal@gmail.com</p>
                    </div>
                  </a>

                  <a
                    href="tel:256-657-8429"
                    className="flex items-center gap-4 p-4 bg-[#F7F8F6] rounded-xl hover:bg-slate-100 transition-colors"
                    data-testid="contact-phone-link"
                  >
                    <div className="w-12 h-12 bg-[#0B1F3A] flex items-center justify-center rounded-xl flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Phone</p>
                      <p className="font-body text-[#172033] font-medium">(256) 657-8429</p>
                    </div>
                  </a>

                  <a
                    href="https://www.linkedin.com/in/cameron-whitlow-66a7ba185"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-[#F7F8F6] rounded-xl hover:bg-slate-100 transition-colors"
                    data-testid="contact-linkedin-link"
                  >
                    <div className="w-12 h-12 bg-[#0B1F3A] flex items-center justify-center rounded-xl flex-shrink-0">
                      <Linkedin className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">LinkedIn</p>
                      <p className="font-body text-[#172033] font-medium">Connect with Cameron</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Downloads */}
              <div>
                <h3 className="font-display text-xl font-bold text-[#172033] mb-6">Resources</h3>
                <div className="space-y-3">
                  {downloads.map((download, index) => (
                    <button
                      key={index}
                      className="w-full flex items-center gap-4 p-4 bg-[#F7F8F6] rounded-xl hover:bg-slate-100 transition-colors text-left"
                      data-testid={`download-${index}`}
                    >
                      <div className="w-10 h-10 bg-[#4F8A2F]/20 flex items-center justify-center rounded-xl flex-shrink-0">
                        <download.icon className="w-5 h-5 text-[#4F8A2F]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-grow">
                        <p className="font-body text-sm font-medium text-[#172033]">{download.title}</p>
                        <p className="text-xs text-slate-500">{download.desc}</p>
                      </div>
                      <Download className="w-4 h-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendly Placeholder */}
              <div className="bg-[#0B1F3A] rounded-2xl p-6 text-center">
                <Calendar className="w-10 h-10 text-[#4F8A2F] mx-auto mb-4" />
                <h3 className="font-display text-lg font-bold text-white mb-2">Prefer to Book Directly?</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Schedule a 30-minute strategy session at your convenience.
                </p>
                <Button className="w-full bg-[#4F8A2F] hover:bg-[#6FAE3A] text-[#0B1F3A] rounded-full font-body">
                  View Calendar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
